import { useState } from 'react';
import { Button, CircularProgress } from '@cere/cere-design-system';
import type { Member } from '@cef-ai/vault-sdk';
import { stepUpRequiredError, toDisplayError } from '@/lib/vault/step-up-error';
import { useVaultSession } from '@/src/state/session';
import { OrgSwitcher } from './OrgSwitcher';
import { OrgRoster } from './OrgRoster';
import { OrgSetupWizard } from './OrgSetupWizard';
import { AddMemberDialog } from './AddMemberDialog';
import { EditMemberGrantsDialog, type DisclosedMember } from './EditMemberGrantsDialog';
import { useOrgDetail } from './useOrgDetail';
import { useMemberCeilings } from './useMemberCeilings';
import { OrgMemoryBankSection } from './memory/OrgMemoryBankSection';
import '@/src/styles/orgs.css';

/**
 * The Organizations section of the one-scroll home (anchor `section-orgs`):
 * the vault-context switcher plus, for the selected vault context, its scopes
 * and member roster. Reads ride the Bearer read client (`session.readSdk`);
 * the write surfaces here — the "Set up organization" wizard, the member
 * invite, the per-member grants editor and the privacy-ceiling picker — sign
 * strictly inside their run clicks, each flow gated on the ceremony surface
 * before it opens (the picker's flow starts at its choice click, so that is
 * where its gate runs).
 *
 * My vault is one of the contexts, not a dead end: the owner entry from the
 * boot join drives the SAME roster + editor surface the orgs get, because
 * the owner is the one principal whose grant-document signature the member
 * writes verify against (plan ruling, fix round 1).
 */
export function OrgsSection() {
  const session = useVaultSession();
  const { orgs, orgsLoaded, orgsFailed, activeOrgVaultId, setActiveOrgVaultId, readSdk } = session;
  // Which vault the section is showing: the owner entry for the My-vault
  // selection (null), the joined org for an org row. A selection a LOADED,
  // successful org list can no longer resolve is explained below (the
  // drop notice) rather than silently blanked — but a list that has not
  // resolved yet, or failed, rules nothing out.
  const selected = activeOrgVaultId === null
    ? orgs.find((o) => o.isOwner) ?? null
    : orgs.find((o) => o.vaultId === activeOrgVaultId && !o.isOwner) ?? null;
  const selectionDropped = orgsLoaded && !orgsFailed && activeOrgVaultId !== null && selected === null;
  const detail = useOrgDetail(readSdk, selected ? selected.vaultId : null);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [grantsMember, setGrantsMember] = useState<DisclosedMember | null>(null);
  const [gateError, setGateError] = useState<string | null>(null);
  // The wizard's write branch keys off the wallet's own vault. The session
  // record is that authority; the switcher's owner entry is the same fact as
  // the boot join observed it, so it stands in when a double carries no
  // record — one source of truth per surface, not two derivations.
  const ownVaultId = session.vault?.vaultId ?? orgs.find((o) => o.isOwner)?.vaultId ?? null;
  // Member writes are owner-or-admin territory: the member PUT's route admits
  // those two only, so a plain member's "Add member" would be a guaranteed
  // 403 behind a live-looking button. On My vault the caller IS the owner —
  // the one signer the grant document verifies against — so the surface is
  // fully theirs; on a joined org the switcher entry carries the role, and an
  // invited admin's writes are still server-refused (their key is not the
  // org owner's) — surfaced as the server states them, not hidden.
  const canManageMembers = selected !== null && (selected.isOwner || selected.role === 'admin');
  // The members' SIGNED ceilings ride the Bearer client next to the detail —
  // the roster row does not carry them. Skipped entirely for a plain member:
  // the picker cannot render on their selection, so the read would be a GET
  // whose values nothing consumes.
  const ceilings = useMemberCeilings(readSdk, selected ? selected.vaultId : null, canManageMembers);

  // One "member data changed" re-read for every committed member write: the
  // roster for add/grants, the signed ceilings for a ceiling write and for a
  // new member (whose freshly-signed v1 grant makes them "None signed", not
  // Unknown). A null map renders Unknown everywhere, which is honest while
  // the re-read is in flight.
  function reloadMemberData() {
    detail.reload();
    ceilings.reload();
  }

  // Ceremony-surface gate BEFORE any flow that signs (the wizard's run, the
  // member PUT, the grants PUT), same ordering discipline as the claim path
  // (lib/clients/vault/claim.ts): if a later signature would need a popup the
  // browser will block, the right moment to find out is at the click that
  // starts the flow — not mid-dialog. A synchronous property read, so the
  // gate spends none of the activation budget it is protecting.
  function gateThen(open: () => void) {
    if (session.wallet.ceremonySurface() === 'popup') {
      setGateError(toDisplayError(stepUpRequiredError()));
      return;
    }
    setGateError(null);
    open();
  }

  function openEditGrants(member: Member) {
    gateThen(() => {
      // Type-level narrowing of the roster's disclosure rule: the row's edit
      // affordance exists only when `scopes` is present, and the editor
      // refuses an undisclosed row (it seeds from the grants).
      if (member.scopes === undefined) return;
      setGrantsMember(member as DisclosedMember);
    });
  }

  return (
    <div className="vault-orgs">
      <div className="vault-orgs__head">
        <div className="vault-orgs__head-text">
          <div className="vault-orgs__eyebrow">Shared vaults</div>
          <h2>Organizations</h2>
          <p className="vault-orgs__sub">
            Switch between your personal vault and the organization vaults you belong to.
            On your own vault you manage the roster and each member’s grants; on an org you joined,
            member changes carry the org owner’s signature — when the server refuses one, it says why.
          </p>
        </div>
        <Button variant="primary" onClick={() => gateThen(() => setWizardOpen(true))}>Set up organization</Button>
      </div>

      <OrgSwitcher
        orgs={orgs}
        loaded={orgsLoaded}
        failed={orgsFailed}
        activeOrgVaultId={activeOrgVaultId}
        onSelect={setActiveOrgVaultId}
        myVaultName={session.vaultDisplayName}
      />

      {gateError && <p className="vault-orgs__detail-error" role="alert">{gateError}</p>}

      {activeOrgVaultId === null && (
        <p className="vault-orgs__note" data-testid="orgs-myvault-note">
          {selected
            ? 'This is your vault — you own it. Its scopes and member roster are below, and you manage them here; a vault becomes an organization the moment it has a second member.'
            : 'This is your personal vault. Select an organization above to see its scopes and its member roster.'}
        </p>
      )}

      {selectionDropped && (
        // The refreshed list no longer returns the selected vault: the
        // membership was revoked (or the vault itself is gone). Say so and
        // offer the way back, instead of a silent blank where the detail
        // used to be. Not `role="alert"`: nothing failed — this is state,
        // announced politely beside the switcher that still works.
        <div className="vault-orgs__dropped" data-testid="orgs-selection-dropped" role="status">
          <p className="vault-orgs__note">
            You are no longer a member of this organization — its roster and memory bank are no
            longer available to this wallet.
          </p>
          <Button variant="tertiary" size="small" onClick={() => setActiveOrgVaultId(null)}>
            Back to My vault
          </Button>
        </div>
      )}

      {/* Spinner only for a detail that is NOT yet on screen: a reload of the
          same target keeps the previous detail mounted (see useOrgDetail), so
          a committed member write must not blank the roster — or unmount the
          memory bank inside it — while the re-read runs. */}
      {detail.loading && detail.detail === null ? (
        <div className="vault-orgs__detail-loading"><CircularProgress size={22} /></div>
      ) : detail.error ? (
        // In-section, not a global banner: an org read failing says nothing
        // about the rest of the session, and the switcher stays usable.
        <p className="vault-orgs__detail-error" role="alert">{detail.error}</p>
      ) : detail.detail ? (
        <div className="vault-orgs__detail">
          <div className="vault-orgs__scopes">
            <h3 className="vault-orgs__subhead">Scopes</h3>
            {detail.detail.scopes.length === 0 ? (
              <p className="vault-orgs__note">This vault has no scopes.</p>
            ) : (
              <div className="vault-orgs__scope-list">
                {detail.detail.scopes.map((s) => (
                  <span key={s.name} className="vault-orgs__scope">{s.displayName ?? s.name}</span>
                ))}
              </div>
            )}
          </div>
          <div className="vault-orgs__members">
            <div className="vault-orgs__members-head">
              <h3 className="vault-orgs__subhead">Members</h3>
              {canManageMembers && (
                <Button variant="tertiary" size="small" onClick={() => gateThen(() => setAddMemberOpen(true))}>
                  Add member
                </Button>
              )}
            </div>
            <OrgRoster
              members={detail.detail.members}
              onEditGrants={canManageMembers ? openEditGrants : undefined}
              ceilingControl={canManageMembers && selected !== null ? {
                vaultId: selected.vaultId,
                // A failed/pending ceilings read leaves the control in place —
                // the write does not read this map — with Unknown as the shown
                // value rather than dropping the affordance on a transient error.
                ceilings: ceilings.ceilings ?? {},
                onSaved: reloadMemberData,
              } : undefined}
            />
            {canManageMembers && ceilings.error && (
              // In-roster, not a banner: the roster itself is unaffected, and
              // the pickers degrade to Unknown rather than to nothing.
              <p className="vault-orgs__detail-error" role="alert">
                Privacy ceilings could not be read — showing Unknown. {ceilings.error}
              </p>
            )}
          </div>
          {/* The org's memory bank — read-only browse, mounted with NO role
              gate: owner and member get the same surface and the server's
              gate (grants + privacy ceiling) does the filtering, so a member
              simply sees fewer rows — that absence is the product, not an
              error to hide. */}
          <OrgMemoryBankSection readSdk={readSdk} vaultId={detail.detail.vaultId} />
        </div>
      ) : null}

      {/* Closing the wizard re-reads the org detail: the wizard refreshes the
          SESSION's scopes and org list, but this section's scopes+roster render
          from the detail hook's own state — without the reload, a finished run
          leaves the section showing the world before it. Cancel pays the same
          one Bearer fetch; a wasted read is cheaper than a stale view. */}
      <OrgSetupWizard
        open={wizardOpen}
        ownVaultId={ownVaultId}
        onClose={() => {
          setWizardOpen(false);
          detail.reload();
        }}
      />

      {selected && (
        <>
          <AddMemberDialog
            open={addMemberOpen}
            vaultId={selected.vaultId}
            // The guard's roster snapshot: while a reload keeps the previous
            // detail mounted it may lag one write behind, which the server's
            // own upsert semantics absorb — the guard is a UI tripwire, not
            // the source of truth.
            members={detail.detail?.members ?? []}
            onAdded={reloadMemberData}
            onClose={() => setAddMemberOpen(false)}
          />
          {grantsMember && detail.detail && (
            <EditMemberGrantsDialog
              // One mount per member: the level state seeds from the row's
              // grants on mount, so switching rows without a remount would
              // edit the previous member under the next one's name.
              key={grantsMember.memberPubkey}
              open
              vaultId={selected.vaultId}
              member={grantsMember}
              orgScopes={detail.detail.scopes}
              onSaved={reloadMemberData}
              onClose={() => setGrantsMember(null)}
            />
          )}
        </>
      )}
    </div>
  );
}
